import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, PhotoIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import { AssetType, IProjectAsset, AssetGenerationStatus } from '@packages/global';
import { useAssets } from '../hooks/useAssets';
import { useDialog } from '../hooks/useDialog';
import { useAssetGenerationWebSocket } from '../hooks/useAssetGenerationWebSocket';
import { useImageGenerationWebSocket } from '../hooks/useImageGenerationWebSocket';
import { SubjectAssetEditDrawer, AssetGrid, AssetGenerateModalV2 } from '../components/projectAssets';
import { toast } from 'react-hot-toast';
import VoiceLibraryTab from '../components/voiceLibrary/VoiceLibraryTab';

type TabKey = AssetType | 'voice';

export default function SubjectLibraryPage() {
  const { t } = useTranslation(['library', 'common']);
  const [selectedTab, setSelectedTab] = useState<TabKey>(AssetType.CHARACTER);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<IProjectAsset | null>(null);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generatingAsset, setGeneratingAsset] = useState<IProjectAsset | null>(null);
  const [generateMode, setGenerateMode] = useState<'text' | 'image'>('text');
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const { loading, createAsset, updateAsset, deleteAsset, getAssetsByType, updateAssetLocal, refreshAsset } = useAssets();
  const { confirm } = useDialog();

  const assetTypeOptions = [
    { type: AssetType.CHARACTER as TabKey, label: t('library:subject.character'), icon: PhotoIcon },
    { type: AssetType.PROP as TabKey, label: t('library:subject.prop'), icon: PhotoIcon },
    { type: AssetType.SCENE as TabKey, label: t('library:subject.scene'), icon: PhotoIcon },
    { type: 'voice' as TabKey, label: t('library:subject.voice'), icon: MusicalNoteIcon },
  ];

  const handleAssetCompleted = useCallback((assetId: string, _imageUrl?: string) => {
    toast.success(t('library:subject.toast.imageGenCompleted'));
    refreshAsset(assetId);
  }, [refreshAsset, t]);

  const handleAssetFailed = useCallback((assetId: string, error: string) => {
    toast.error(t('library:subject.toast.imageGenFailed', { error }));
    updateAssetLocal(assetId, { generationStatus: AssetGenerationStatus.FAILED });
  }, [updateAssetLocal, t]);

  const handleProgressUpdate = useCallback((data: any) => {
    if (data.assetId) {
      updateAssetLocal(data.assetId, {
        generationStatus: data.status === 'completed'
          ? AssetGenerationStatus.COMPLETED
          : data.status === 'failed'
            ? AssetGenerationStatus.FAILED
            : AssetGenerationStatus.GENERATING
      });
    }
  }, [updateAssetLocal]);

  useAssetGenerationWebSocket({
    enabled: selectedTab !== 'voice',
    onAssetCompleted: handleAssetCompleted,
    onAssetFailed: handleAssetFailed,
    onProgressUpdate: handleProgressUpdate
  });

  useImageGenerationWebSocket();

  const isAssetTab = selectedTab !== 'voice';
  const currentAssetType = isAssetTab ? (selectedTab as AssetType) : AssetType.CHARACTER;
  const currentAssets = isAssetTab ? getAssetsByType(currentAssetType) : [];

  const getAssetTypeLabel = (type: AssetType) => {
    return assetTypeOptions.find(opt => opt.type === type)?.label || t('library:subject.emptyAction');
  };

  const handleCreateAsset = () => {
    setEditingAsset(null);
    setIsDrawerOpen(true);
  };

  const handleEditAsset = (asset: IProjectAsset) => {
    setEditingAsset(asset);
    setIsDrawerOpen(true);
  };

  const handleSaveAsset = async (data: any) => {
    const success = editingAsset
      ? await updateAsset(editingAsset.id, data)
      : await createAsset(data);
    if (success) {
      setIsDrawerOpen(false);
      setEditingAsset(null);
    }
  };

  const handleDeleteAsset = async (asset: IProjectAsset) => {
    const confirmed = await confirm(t('library:subject.deleteConfirm', { name: asset.name }), t('common:confirmDelete'), 'danger');
    if (confirmed) deleteAsset(asset.id);
  };

  const handleGenerateAsset = (asset: IProjectAsset) => {
    setGeneratingAsset(asset);
    setGenerateMode('text');
    setGenerateModalOpen(true);
  };

  const handleUploadAsset = (asset: IProjectAsset) => {
    setGeneratingAsset(asset);
    setGenerateMode('image');
    setGenerateModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-bg-page text-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-50">{t('library:subject.title')}</h1>
            <p className="text-gray-400 mt-2">{t('library:subject.subtitle')}</p>
          </div>
          <button
            onClick={isAssetTab ? handleCreateAsset : () => setVoiceModalOpen(true)}
            className="bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all cursor-pointer"
          >
            <PlusIcon className="h-5 w-5" />
            {isAssetTab ? t('library:subject.addType', { type: getAssetTypeLabel(currentAssetType) }) : t('library:voice.create')}
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border-subtle mb-8">
          <div className="flex space-x-8">
            {assetTypeOptions.map((option) => (
              <button
                key={option.type}
                onClick={() => setSelectedTab(option.type)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors cursor-pointer ${selectedTab === option.type
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <option.icon className="h-5 w-5" />
                  {option.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {isAssetTab ? (
          <>
            <AssetGrid
              assets={currentAssets}
              loading={loading}
              onEdit={handleEditAsset}
              onDelete={handleDeleteAsset}
              onGenerate={handleGenerateAsset}
              onUpload={handleUploadAsset}
              emptyText={t('library:subject.empty', { type: getAssetTypeLabel(currentAssetType) })}
              emptySubText={t('library:subject.emptySubText', { type: getAssetTypeLabel(currentAssetType) })}
              onEmptyAction={handleCreateAsset}
              emptyActionText={t('library:subject.emptyAction')}
            />

            <SubjectAssetEditDrawer
              isOpen={isDrawerOpen}
              onClose={() => { setIsDrawerOpen(false); setEditingAsset(null); }}
              title={editingAsset ? t('library:subject.editType', { type: getAssetTypeLabel(editingAsset.assetType) }) : t('library:subject.addType', { type: getAssetTypeLabel(currentAssetType) })}
              initialData={editingAsset || undefined}
              defaultAssetType={currentAssetType}
              onSave={handleSaveAsset}
            />

            <AssetGenerateModalV2
              isOpen={generateModalOpen}
              onClose={() => { setGenerateModalOpen(false); setGeneratingAsset(null); }}
              subjectType={generatingAsset?.assetType || AssetType.CHARACTER}
              subjectName={generatingAsset?.name || ''}
              subjectDescription={generatingAsset?.description}
              initialPrompt={generatingAsset?.prompt}
              artStyle={generatingAsset?.artStyle || ''}
              existingImageUrl={generatingAsset?.conceptImageUrl || generatingAsset?.frontViewUrl}
              initialMode={generateMode}
              onConfirm={async (result) => {
                if (generatingAsset) {
                  try {
                    await updateAsset(generatingAsset.id, {
                      conceptImageUrl: result.imageUrl,
                      prompt: result.prompt,
                      ...(result.subjectDescription !== undefined && { description: result.subjectDescription })
                    });
                    toast.success(t('library:subject.toast.imageUpdated'));
                  } catch (err: any) {
                    toast.error(err.message || t('library:subject.toast.imageUpdateFailed'));
                  }
                }
              }}
              onSuccess={() => {
                if (generatingAsset) {
                  updateAssetLocal(generatingAsset.id, { generationStatus: AssetGenerationStatus.PENDING });
                }
              }}
            />
          </>
        ) : (
          <VoiceLibraryTab modalOpen={voiceModalOpen} onModalClose={() => setVoiceModalOpen(false)} />
        )}
      </div>
    </div>
  );
}
